import { defineComponent } from 'vue';
import jobLinkFetchHeader from 'javascript/lib/job_link_fetch_header';

const BasePicker = defineComponent({
  name: 'BasePicker',

  props: {
    // Sets property on HTML input.
    title: {
      type: String,
      default: null,
    },
    // Sets property on HTML input.
    maxlength: {
      type: String,
      default: null,
    },
    // Sets property on HTML input.
    placeholder: {
      type: String,
      default: null,
    },
    // Sets property on HTML input.
    readonly: {
      type: Boolean,
      default: null,
    },
    // Sets property on HTML input.
    required: {
      type: Boolean,
      default: null,
    },
    // Optional (''). Initial value to use for the component.
    inputValue: {
      type: String,
      default: '',
    },
    // Optional (true). Controls if input is hidden.
    inputShown: {
      type: Boolean,
      default: true,
    },
    // Optional (''). Overrides default display behavior by updating label
    // elsewhere on the page (direct DOM, for non-Vue rendered markup).
    labelExternalId: {
      type: String,
      default: '',
    },
    // Optional (true). Controls if label is displayed after the input.
    showLabel: {
      type: Boolean,
      default: true,
    },
    // Optional ('code'). Controls how selection is mapped to the entry box.
    textMapping: {
      type: String,
      default: 'code',
    },
    valueMode: {
      type: String,
      default: 'id',
    },
    // Optional ({}). Value object (preferred) or JSON string to additionally pass to the
    // API the auto-complete component uses. Can be used to apply specific scopes
    // or otherwise control the results.
    apiArguments: {
      validator(val) {
        return val !== null;
      },
      default() {
        return {};
      },
    },
    inputId: {
      type: String,
      required: true,
    },
    inputName: {
      type: String,
      required: true,
    },
  },

  emits: ['focus', 'blur', 'change'],

  data() {
    return {
      chosen: null,
      cache: {},
      text: '',
      focused: false,
      loading: 0,
      justSelected: false,
      origText: null,
      matches: [],
      suggestions: [],
      showSuggestions: false,
      highlightedIndex: -1,
      inputRect: null,
      blurTimeout: null,
    };
  },

  computed: {
    shownLabel() {
      return this.chosen && this.showLabel && this.labelExternalId === '';
    },
    fieldValue() {
      const {
        chosen,
        valueMode,
      } = this;

      if (chosen == null) return '';
      return chosen[valueMode] || chosen.id;
    },
    pickerName() {
      const match = this.inputName.match(/^(.+)\[([^\]]+)\]$/);
      if (match) {
        return `${match[1]}[${match[2]}_picker]`;
      }
      return `${this.inputName}_picker`;
    },
    pickerId() {
      return `${this.inputId}_picker`;
    },
    apiData() {
      const { apiArguments } = this;
      let obj = apiArguments || {};

      if (Array.isArray(obj)) {
        // eslint-disable-next-line no-console
        console.warn(
          'API arguments should be supplied as an object or string, not array',
        );
        obj = {};
      } else if (typeof apiArguments === 'string') {
        obj = JSON.parse(apiArguments);
      }

      return { ...obj, name: this.text.trim(), original: this.origText };
    },
    dropdownStyle() {
      if (!this.inputRect) return {};
      return {
        position: 'fixed',
        top: `${this.inputRect.bottom}px`,
        left: `${this.inputRect.left}px`,
        width: `${this.inputRect.width}px`,
      };
    },
  },

  watch: {
    chosen() {
      this.$emit('change', this.fieldValue);
    },
  },

  beforeMount() {
    this.setupFromDefaultInput();
  },

  mounted() {
    this.shiftLabelFor();
    this.observeAriaControls();
    window.addEventListener('scroll', this.handleScroll, true);
  },

  beforeUnmount() {
    if (this.ariaControlsObserver) {
      this.ariaControlsObserver.disconnect();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    window.removeEventListener('scroll', this.handleScroll, true);
  },

  methods: {
    shiftLabelFor() {
      const el = $(`label[for="${this.inputId}"]`);
      const pickerLabel = this.pickerId;
      if (el.length) {
        el.attr('for', pickerLabel);
      }
    },

    populateLookupData() {
      return this.acquireLookupData(null).then(arr => {
        const filtered = arr.filter(d => d.visible !== false);
        return filtered;
      });
    },

    fetchOrCacheLookup(requestBody, fetcher) {
      const CACHE_TIMEOUT = 60 * 10000; // 10 minutes
      const entry = this.cache[requestBody];

      if (entry != null && entry.at > Date.now()) {
        // console.info('Cache Hit', requestBody);
        return Promise.resolve(entry.results);
      }

      // console.info('Cache Miss', requestBody);
      return fetcher().then(response => response.json()).then((data) => {
        this.cache[requestBody] = { at: Date.now() + CACHE_TIMEOUT, results: data };
        return data;
      });
    },

    acquireLookupData(bodyData = null) {
      this.loading += 1;

      const requestData = Object.assign({ mode: 'vue' }, bodyData || this.apiData);
      const requestBody = JSON.stringify(requestData);

      return this.fetchOrCacheLookup(
        requestBody,
        () => fetch(this.apiUrl(), {
          method: 'post',
          credentials: 'same-origin',
          headers: jobLinkFetchHeader(),
          body: requestBody,
        }),
      ).then(data => this.processAcquiredLookupData(data)).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Autocomplete fetch failed', error);
        return this.processAcquiredLookupData([]);
      });
    },

    processAcquiredLookupData(data) {
      this.matches = data;
      this.loading -= 1;

      if (this.focused) {
        return data;
      }

      if (this.chosen == null) {
        this.findBestMatch();
      }
      return [];
    },

    onSuggestSelect(result) {
      // Replace search entry with code selected.
      // console.log('select', result);
      if (result == null) return;

      this.text = result[this.textMapping];
      this.justSelected = true;
      this.chosen = result;

      this.$nextTick(() => {
        if (this.$refs.suggestInput) {
          this.$refs.suggestInput.focus();
        }
      });
    },

    onInputEvent(entry) {
      // console.log('input', entry);
      if (entry === '') {
        // Clear match if input cleared always.
        this.matches = [];
        this.chosen = null;
      } else if (this.justSelected) {
        // Fired after selection, so unset flag and ignore.
        this.justSelected = false;
      } else {
        // User changed input since or without list selection.
        this.chosen = null;
      }
    },

    onInputFocus() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout);
        this.blurTimeout = null;
      }

      this.focused = true;
      this.$emit('focus');

      if (this.suggestions.length > 0 && this.text.length >= 1) {
        this.showSuggestions = true;
        this.takeLocation();
      }
    },

    onInput(event) {
      const { value } = event.target;
      this.text = value;

      this.onInputEvent(value);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      if (!value || value.length < 1) {
        this.suggestions = [];
        this.showSuggestions = false;
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions();
      }, 500);
    },

    onInputBlur() {
      this.blurTimeout = setTimeout(() => {
        this.showSuggestions = false;
        this.focused = false;

        if (this.chosen != null) return;

        this.findBestMatch();
        this.$emit('blur');
      }, 200);
    },

    foundMatch(match) {
      this.chosen = match;

      const text = match[this.textMapping];
      if (this.origText === null) { this.origText = text; }

      this.text = text;
    },

    findFirstMatch() {
      const matches = this.matches || [];
      const match = matches[0] || null;

      // console.log('findFirstMatch', match);
      if (match) this.foundMatch(match);
    },

    findBestMatch() {
      const {
        text,
        textMapping,
      } = this;

      const matches = this.matches || [];

      // Preserve valid selection
      if (text === '') return;

      // First try exact match in current matches
      const match = matches.find(m => m[textMapping] === text);

      if (match) {
        // Exact code match found.
        this.foundMatch(match);
      } else {
        // Closest fuzzy match available.
        this.findFirstMatch();
      }
    },

    setupFromDefaultInput() {
      const {
        inputValue,
        valueMode,
      } = this;

      if (inputValue === '' || inputValue === null) return;

      let bodyData = null;
      const forId = valueMode === 'id';

      if (forId) {
        bodyData = { id: inputValue.trim() };
      } else {
        this.origText = inputValue.trim();
        this.text = inputValue.trim();
      }

      this.acquireLookupData(bodyData).then(() => {
        if (forId) {
          this.findFirstMatch();
        } else {
          this.findBestMatch();
        }
      });
    },

    setExternalText(id, nameOfId, prop, val) {
      if (id == null || id === '') return;

      const elem = document.getElementById(id);
      let content = '';
      if (val) {
        if (Array.isArray(prop)) {
          const key = prop.find(p => val[p] != null);
          if (key) content = val[key];
        } else {
          content = val[prop];
        }
      }
      if (content == null) content = '';

      if (elem) {
        elem.textContent = content;
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `${nameOfId} not found`,
          id,
          'to update with',
          content,
        );
      }
    },

    handleScroll(event) {
      if (this.showSuggestions) {
        const dropdown = document.getElementById(`${this.pickerId}-suggestions`);
        if (dropdown && dropdown.contains(event.target)) {
          return;
        }
        this.closeSuggestions();
      }
    },

    takeLocation() {
      this.$nextTick(() => {
        if (this.$refs.suggestInput) {
          this.inputRect = this.$refs.suggestInput.getBoundingClientRect();
        }
      });
    },

    scrollToHighlighted() {
      this.$nextTick(() => {
        const dropdown = document.getElementById(`${this.pickerId}-suggestions`);
        if (!dropdown) return;

        const highlightedItem = dropdown.querySelector('.suggestion-item.highlighted');
        if (highlightedItem) {
          highlightedItem.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          });
        }
      });
    },

    closeSuggestions() {
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    },

    descendantAsign(highlightedIndex, pickerId, optStr) {
      return highlightedIndex >= 0 ? `${pickerId}${optStr}${highlightedIndex}` : null;
    },

    observeAriaControls() {
      this.$nextTick(() => {
        let input = this.$refs.suggestInput;
        // If suggestInput is a component wrapper, get the actual DOM element
        if (input && input.$el) {
          input = input.$el.inputElement || input.$el;
        }
        if (!input) return;
        this.ariaControlsObserver = new MutationObserver(() => {
          if (input.hasAttribute('aria-controls')) {
            const id = input.getAttribute('aria-controls');
            if (!document.getElementById(id)) {
              input.removeAttribute('aria-controls');
            }
          }
        });
        this.ariaControlsObserver.observe(input, { attributes: true, attributeFilter: ['aria-controls'] });
      });
    },

    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
        this.selectSuggestion(this.suggestions[this.highlightedIndex]);
      }
    },

    highlightPrev() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex -= 1;
        this.scrollToHighlighted();
      } else if (this.highlightedIndex === -1 && this.suggestions.length > 0) {
        this.highlightedIndex = this.suggestions.length - 1;
        this.scrollToHighlighted();
      }
    },

    highlightNext() {
      if (this.highlightedIndex < this.suggestions.length - 1) {
        this.highlightedIndex += 1;
        this.scrollToHighlighted();
      }
    },

    selectSuggestion(suggestion) {
      this.onSuggestSelect(suggestion);
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    },

    // This method must exist and be called by VueSimpleSuggest
    apiUrl() {
      throw new Error('apiUrl() must be implemented by child component');
    },
  },
});

export default BasePicker;
